import React from 'react';
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Rating,
  useTheme,
  useMediaQuery,
  Divider,
  Container
} from '@mui/material';
import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const FastTrackWorkshop: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  

  return (
    <Box
      sx={{
        py: 6,
        background: 'linear-gradient(180deg, rgba(155, 199, 232, 1) 12%, rgba(174, 208, 235, 1) 18%, rgba(255, 255, 255, 1) 35%)',
      }}
    >
      <Box >
        <Container maxWidth='xl'>

          {/* HEADING SECTION */}
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            fontWeight="bold" gutterBottom
          >
            Kickstart Your Real Estate Career with the
          </Typography>
          <Typography
            className='akirafont'
            variant={isMobile ? 'h2' : 'h2'}
            fontWeight="bold"
            color="#0056B3"
            textAlign="center"
          >
            FAST–TRACK SALES WORKSHOP
          </Typography>
        </Container>

        {/* CENTER DIVIDER UNDER HEADING */}
        <Box display="flex" justifyContent="center" mt={2} mb={4}>
          <Divider
            sx={{
              width: { xs: '290px', md: '700px', lg: '700px' },
              borderBottomWidth: '2px',
              backgroundColor: '#0f63a5',
            }}
          />
        </Box>

        {/* CONTENT GRID */}
        <Grid container spacing={4} alignItems="center" >
          {/* LEFT CONTENT */}
          <Grid item xs={12} md={7} >
            <Box sx={{ px: { xs: 2, md: 8, lg: 8 } }}>
              <Typography variant={isMobile ? 'h5' : 'h6'} fontWeight="bold" mt={1} textAlign='start'>
                What You Will Unlock in This Workshop :
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  border: '1px solid #0f63a5',
                  borderRadius: 2,
                  p: 2,
                  mt: 2,
                  backgroundColor: '#fff',
                }}
              >
                <List>
                  {[
                    {
                      title: 'Speak Like a Market Leader:',
                      desc: 'Learn to position yourself with authority that commands attention.',
                    },
                    {
                      title: 'Sell Without Sounding Salesy:',
                      desc: 'Build trust-first conversations that convert without pressure.',
                    },
                    {
                      title: 'Handle Objections Like a Pro:',
                      desc: 'Master the art of resolving high-value pushbacks with confidence.',
                    },
                  ].map((item, index) => (
                    <ListItem key={index} alignItems="flex-start" disablePadding sx={{ mb: 2 }}>
                      <ListItemIcon sx={{ minWidth: '32px' }}>
                        <CheckCircleIcon
                          sx={{
                            color: '#0f63a5',
                            boxShadow: '0px 2px 6px rgba(15, 99, 165, 0.4)', // ⭐ Box shadow added
                            borderRadius: '50%',
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography fontWeight="bold" variant="h6">
                            {item.title}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {item.desc}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* RATING SECTION */}
              <Box mt={4}>
                <Typography variant="body1" textAlign="start" sx={{ display: 'flex', alignItems: 'center' }}>
                  Trained &nbsp;
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '14px' }}> Hundreds &nbsp;</Typography> of
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '14px' }}> &nbsp;Top Agents</Typography>
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  {/* "Rated" */}
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#000' }}>
                    Rated
                  </Typography>

                  {/* "4.5/5" */}
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000' }}>
                    4.5/5
                  </Typography>

                  {/* Rating stars */}
                  <Box
                    sx={{
                      p: 0.5,
                      border: '1px solid #000',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                      backgroundColor: '#fff',
                    }}
                  >
                    <Rating value={4.5} readOnly precision={0.5} />
                  </Box>

                  {/* "for Outcomes & Transformation" */}
                  <Typography variant="h6" sx={{ fontWeight: 500, color: '#000' }}>
                    for{' '}
                    <Typography
                      component="span"
                      variant="h6"
                      sx={{ fontWeight: 'bold', color: '#000' }}
                    >
                      Outcomes & Transformation
                    </Typography>
                  </Typography>
                </Box>

              </Box>
            </Box>
          </Grid>

          {/* RIGHT SECTION */}
          <Grid item xs={12} md={5}>
            <Box sx={{ position: 'relative', width: '100%', textAlign: 'center', bottom: 80 }}>
              {/* Background Image Behind RW */}
              <Box
                component="img"
                src="https://dprstorage.b-cdn.net/RW/RW%20LOGO.png"
                alt="Background"
                sx={{
                  position: 'absolute',
                  top: '12%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100%',
                  height: '72%',
                  opacity: 0.1,
                  zIndex: 1,
                }}
              />

              {/* Foreground RW Image */}
              <Box
                component="img"
                src="https://dprstorage.b-cdn.net/RW/rwlandingpageimg.png"
                alt="Rajiv Williams"
                sx={{
                  maxWidth: '100%',
                  height: '600px',
                  borderRadius: 2,
                  position: 'relative',
                  zIndex: 2,
                }}
              />

              {/* Name Box */}
              <Box
                color="white"
                py={2}
                fontWeight="bold"
                sx={{
                  background: 'linear-gradient(90deg,rgba(255,255,255,1) 0%, rgba(15,99,165,1) 50%)',
                  mt: '-30px',
                  position: 'relative',
                  zIndex: 3,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography className='akirafont' variant="h4" component="span" sx={{ color: 'white' }}>
                  RAJIV WILLIAMS
                </Typography>

                <Typography variant="body1" component="span" sx={{ color: 'white', fontWeight: 800, fontSize: '10px', textShadow: '1px 1px 2px rgba(0, 0, 0, 0.6)', }}>
                  15+ Years | 2000 Cr+ Deals Closed | High-Ticket Sales Specialist
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FastTrackWorkshop;
